import * as React from 'react'
import { closeWidgetTourStepModal } from '../../store/modals/actions'
import ModalHeader from './Parts/ModalHeader'
import { AppState } from '../../store'
import { AnyAction, Dispatch } from 'redux'
import { connect } from 'react-redux'
import ModalWindow from './Parts/ModalWindow'
import ModalMiddle from './Parts/ModalMiddle'
import ModalContent from './Parts/ModalContent'
import { useTranslation } from 'react-i18next'
import Button from '../Button/Button'
import Editor, { MESSAGE_EDITOR_CONFIG } from '../Editor/Editor'
import EditorContainer from '../Editor/EditorContainer'
import { WidgetTourStepsController } from '../../controllers'
import { CurrentUser, DisplayableError, WidgetTourStep, WidgetTourStepType } from '../../types'
import TooltipError from '../Tooltips/ErrorTooltip'
import Notification from '../../utilities/Notification'

interface IStateToProps {
  currentUser: CurrentUser
  widgetId: string
  tourId: string
  step?: WidgetTourStep
  onSubmit?: (step: WidgetTourStep) => void
}

interface IDispatchToProps {
  close: typeof closeWidgetTourStepModal
}

type IProps = IDispatchToProps & IStateToProps

interface IState {
  step: WidgetTourStep
  errors: DisplayableError[]
  isSubmitting: boolean
}

const WidgetTourStepModal = (props: IProps) => {
  const { t } = useTranslation()
  const { widgetId, tourId } = props
  const [state, setState] = React.useState<IState>({
    step: props.step || { type: WidgetTourStepType.POST, title: '', description: '', page_path: '/' },
    errors: [],
    isSubmitting: false,
  })
  const { step, errors, isSubmitting } = state

  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, step: { ...step, title: e.currentTarget.value } })
  }

  const onElementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, step: { ...step, element: e.currentTarget.value || null } })
  }

  const onDescriptionChange = (description: string) => {
    setState({ ...state, step: { ...step, description } })
  }

  const onCloseClick = () => {
    props.close()
  }

  const onFormSubmit = async (e) => {
    if (e) e.preventDefault()

    if (!step?.title) return

    setState({ ...state, isSubmitting: true })

    try {
      const action = step.id
        ? WidgetTourStepsController.update(widgetId, tourId, step)
        : WidgetTourStepsController.create(widgetId, tourId, step)

      const response = await action

      if (response.errors) {
        setState({ ...state, errors: response.errors, isSubmitting: false })
      } else {
        Notification.notifySuccess(
          step.id
            ? t('WidgetTourStepModal::Step successfully updated')
            : t('WidgetTourStepModal::Step successfully added')
        )
        if (props.onSubmit) props.onSubmit(response)
        props.close()
      }
    } catch (ex) {
      console.error(ex)
      setState({ ...state, isSubmitting: false })
    }
  }

  const onErrorsDismiss = () => {
    setState({ ...state, errors: [] })
  }

  return (
    <ModalWindow>
      <ModalHeader
        title={step?.id ? t('WidgetTourStepModal::Edit step') : t('WidgetTourStepModal::New step')}
        onCloseClick={onCloseClick}
      />

      <ModalMiddle>
        <ModalContent>
          <form onSubmit={onFormSubmit}>
            <div className='form-item'>
              <label>{t('WidgetTourStepModal::Title')} <span>*</span></label>
              <input
                type='text'
                value={step?.title || ''}
                onChange={onTitleChange}
                placeholder={t('WidgetTourStepModal::Enter a title')}
              />
            </div>
            <div className='form-item'>
              <label>{t('WidgetTourStepModal::Element')}</label>
              <input
                type='text'
                value={step?.element || ''}
                onChange={onElementChange}
                placeholder={t('WidgetTourStepModal::CSS selector, e.g. #element-id or .element-class')}
              />
            </div>
            <div className='form-item'>
              <label>{t('WidgetTourStepModal::Description')}</label>
              <EditorContainer>
                <Editor
                  model={step?.description || ''}
                  onModelChange={onDescriptionChange}
                  config={{
                    ...MESSAGE_EDITOR_CONFIG,
                    toolbarButtons: [...MESSAGE_EDITOR_CONFIG.toolbarButtons, 'insertVideo'],
                    placeholderText: t('WidgetTourStepModal::Enter a description'),
                    heightMin: 120,
                    heightMax: 240,
                  }}
                />
              </EditorContainer>
            </div>
            <input type='submit' style={{ display: 'none' }} onClick={onFormSubmit} />
          </form>
        </ModalContent>

        <div className='modal-footer'>
          <div />
          <div key='main-action' className='popover-wrapper'>
            <TooltipError
              errors={errors}
              onDismiss={onErrorsDismiss}
            />
            <Button
              type='success'
              text={step?.id ? t('WidgetTourStepModal::Update') : t('WidgetTourStepModal::Add step')}
              onClick={onFormSubmit}
              disabled={!step?.title || isSubmitting}
            />
          </div>
        </div>
      </ModalMiddle>
    </ModalWindow>
  )
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: { currentUser },
    modals: {
      widgetTourStepModal: {
        widgetId,
        tourId,
        step,
        onSubmit,
      }
    }
  } = state

  return {
    currentUser,
    widgetId,
    tourId,
    step,
    onSubmit,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    close: () => dispatch(closeWidgetTourStepModal()),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(WidgetTourStepModal)
